import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { TokenStorageService } from '../../services/storage/token-storage.service';
import { MaintenanceService, MaintenanceEvent } from '../../services/maintenance.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-maintenance-scheduling',
    templateUrl: './maintenance-scheduling.component.html',
    styleUrls: ['./maintenance-scheduling.component.css'],
    standalone: false
})
export class MaintenanceSchedulingComponent implements OnInit, OnDestroy {
  user: any;
  isAdmin: boolean = false;
  maintenanceForm!: FormGroup;
  scheduledMaintenances: MaintenanceEvent[] = [];
  loading: boolean = false;
  submitting: boolean = false;
  
  // Calendar view state
  currentDate: Date = new Date();
  calendarEvents: any[] = [];
  
  // Selected regions
  selectedRegions: Set<string> = new Set();
  
  private maintenanceSubscription: Subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private tokenStorageService: TokenStorageService,
    private maintenanceService: MaintenanceService,
    private toastr: ToastrService
  ) {
    this.initializeForm();
    this.user = this.tokenStorageService.getUser();
    this.isAdmin = this.user?.role === 'admin';
  }

  ngOnInit(): void {
    if (!this.isAdmin) {
      this.toastr.error('Access denied. Admin privileges required.', 'Unauthorized');
      return;
    }

    this.loadScheduledMaintenances();
  }

  ngOnDestroy(): void {
    if (this.maintenanceSubscription) {
      this.maintenanceSubscription.unsubscribe();
    }
  }

  initializeForm(): void {
    this.maintenanceForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: ['', [Validators.required, Validators.minLength(10)]],
      scheduledFor: ['', Validators.required],
      durationHours: [1, [Validators.required, Validators.min(0), Validators.max(72)]],
      durationMinutes: [0, [Validators.required, Validators.min(0), Validators.max(59)]],
      affectedRegions: [[], this.regionsValidator],
      impact: ['minor', Validators.required], // minor, major, critical
      notifySubscribers: [true]
    }, { validators: this.durationValidator });
  }

  private loadScheduledMaintenances(): void {
    this.loading = true;
    this.maintenanceSubscription = this.maintenanceService.getScheduledMaintenances().subscribe({
      next: (response) => {
        this.scheduledMaintenances = response.scheduled_maintenances || [];
        this.prepareCalendarEvents();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading scheduled maintenances:', error);
        this.toastr.error('Failed to load scheduled maintenances', 'Error');
        this.loading = false;
      }
    });
  }

  private prepareCalendarEvents(): void {
    this.calendarEvents = this.scheduledMaintenances.map(maintenance => ({
      id: maintenance.id,
      title: maintenance.name,
      start: new Date(maintenance.scheduled_for),
      end: new Date(maintenance.scheduled_until),
      status: maintenance.status,
      description: maintenance.incident_updates?.[0]?.body || '',
      color: this.getEventColor(maintenance.status)
    }));
  }

  private getEventColor(status: string): string {
    switch (status) {
      case 'scheduled': return '#ffa726';
      case 'in_progress': return '#ef5350';
      case 'completed': return '#66bb6a';
      default: return '#90a4ae';
    }
  }

  // Custom validator to ensure at least some duration is provided
  durationValidator(form: FormGroup): { [key: string]: any } | null {
    const hours = form.get('durationHours')?.value || 0;
    const minutes = form.get('durationMinutes')?.value || 0;
    
    if (hours === 0 && minutes === 0) {
      return { invalidDuration: true };
    }
    
    return null;
  }

  // Custom validator to ensure at least one region is selected
  regionsValidator = (control: any): { [key: string]: any } | null => {
    const value = control.value;
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return { required: true };
    }
    return null;
  }

  // Handle region checkbox changes
  onRegionChange(region: string, event: any): void {
    if (event.target.checked) {
      this.selectedRegions.add(region);
    } else {
      this.selectedRegions.delete(region);
    }
    
    // Update form control value with array of regions
    const regionsArray = Array.from(this.selectedRegions);
    this.maintenanceForm.patchValue({
      affectedRegions: regionsArray
    });
    
    // Trigger validation
    this.maintenanceForm.get('affectedRegions')?.updateValueAndValidity();
  }

  getDurationError(): string | null {
    if (this.maintenanceForm.hasError('invalidDuration')) {
      return 'Maintenance duration must be at least 1 minute';
    }
    return null;
  }

  onSubmit(): void {
    if (this.maintenanceForm.valid && !this.submitting) {
      this.submitting = true;
      const formData = this.maintenanceForm.value;
      
      // Calculate scheduled_until from scheduledFor + duration
      const scheduledFor = new Date(formData.scheduledFor);
      const durationHours = parseInt(formData.durationHours) || 0;
      const durationMinutes = parseInt(formData.durationMinutes) || 0;
      
      const scheduledUntil = new Date(scheduledFor);
      scheduledUntil.setHours(scheduledUntil.getHours() + durationHours);
      scheduledUntil.setMinutes(scheduledUntil.getMinutes() + durationMinutes);

      // Validate dates
      if (scheduledFor <= new Date()) {
        this.toastr.error('Maintenance cannot be scheduled for the past', 'Invalid Date');
        this.submitting = false;
        return;
      }

      if (durationHours === 0 && durationMinutes === 0) {
        this.toastr.error('Maintenance duration must be at least 1 minute', 'Invalid Duration');
        this.submitting = false;
        return;
      }

      const maintenanceData = {
        name: formData.name,
        body: formData.description,
        scheduled_for: scheduledFor.toISOString(),
        scheduled_until: scheduledUntil.toISOString(),
        status: 'scheduled',
        impact_override: formData.impact,
        deliver_notifications: formData.notifySubscribers,
        region: formData.affectedRegions || [] // Send regions as array like ["MENA","ASIA"]
      };

      this.maintenanceService.createMaintenanceIncident(maintenanceData).subscribe({
        next: (response) => {
          this.toastr.success('Maintenance scheduled successfully', 'Success');
          this.maintenanceForm.reset();
          this.selectedRegions.clear(); // Clear selected regions
          this.initializeForm();
          this.loadScheduledMaintenances(); // Refresh the list
          this.submitting = false;
        },
        error: (error) => {
          console.error('Error creating maintenance:', error);
          this.toastr.error('Failed to create maintenance incident', 'Error');
          this.submitting = false;
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.maintenanceForm.controls).forEach(key => {
      const control = this.maintenanceForm.get(key);
      control?.markAsTouched();
    });
  }

  resetForm(): void {
    this.maintenanceForm.reset();
    this.selectedRegions.clear();
    this.initializeForm();
    // Reset checkboxes
    const menaCheckbox = document.getElementById('mena') as HTMLInputElement;
    const asiaCheckbox = document.getElementById('asia') as HTMLInputElement;
    if (menaCheckbox) menaCheckbox.checked = false;
    if (asiaCheckbox) asiaCheckbox.checked = false;
  }

  // Calendar navigation methods
  previousMonth(): void {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1);
  }

  nextMonth(): void {
    this.currentDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1);
  }

  getCurrentMonthYear(): string {
    return this.currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  getCalendarDays(): Date[] {
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();
    
    // Get first day of the month and how many days are in this month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    // Get the day of week for the first day (0 = Sunday)
    const startingDayOfWeek = firstDay.getDay();
    
    // Create array of days to display
    const days: Date[] = [];
    
    // Add days from previous month to fill the calendar grid
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push(new Date(year, month, -i));
    }
    
    // Add all days of current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }
    
    // Add days from next month to complete the grid (up to 42 days total)
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push(new Date(year, month + 1, day));
    }
    
    return days;
  }

  getEventsForDate(date: Date): any[] {
    return this.calendarEvents.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      return date >= eventStart && date <= eventEnd;
    });
  }

  isCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.currentDate.getMonth();
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  deleteMaintenance(maintenanceId: string): void {
    if (confirm('Are you sure you want to delete this maintenance schedule?')) {
      this.maintenanceService.deleteMaintenanceIncident(maintenanceId).subscribe({
        next: () => {
          this.toastr.success('Maintenance deleted successfully', 'Success');
          this.loadScheduledMaintenances();
        },
        error: (error) => {
          console.error('Error deleting maintenance:', error);
          this.toastr.error('Failed to delete maintenance', 'Error');
        }
      });
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  }
} 